/**
 * GraphFloatingControls
 *
 * Absolute-positioned overlay controls rendered on top of the graph canvas:
 *   Bottom-left  → Import Material, Import Workflow
 *   Bottom-right → Zoom In, Zoom Out, Fit, Actual Size (vertical stack)
 */

import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CropFreeIcon from '@mui/icons-material/CropFree';
import CropSquareIcon from '@mui/icons-material/CropSquare';

export interface GraphFloatingControlsProps {
  isLocked: boolean;
  isArchived: boolean;
  isArchivalInProgress: boolean;
  canImport: boolean;
  onImportMaterial: () => void;
  onImportWorkflow: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomFit: () => void;
  onZoomActual: () => void;
}

export const GraphFloatingControls: React.FC<GraphFloatingControlsProps> = ({
  isLocked,
  isArchived,
  isArchivalInProgress,
  canImport,
  onImportMaterial,
  onImportWorkflow,
  onZoomIn,
  onZoomOut,
  onZoomFit,
  onZoomActual,
}) => {
  const importDisabled = !canImport || isLocked || isArchived || isArchivalInProgress;

  return (
    <>
      {/* ── Bottom-left: Import buttons ──────────────────────────────── */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          zIndex: 10,
        }}
      >
        <Button
          variant="contained"
          size="small"
          disabled={importDisabled}
          startIcon={<ScienceOutlinedIcon fontSize="small" />}
          onClick={onImportMaterial}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: 2,
            '&:hover': { backgroundColor: 'action.hover', boxShadow: 3 },
            '&.Mui-disabled': { backgroundColor: 'background.paper' },
          }}
        >
          Add Material
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={importDisabled}
          startIcon={<AccountTreeOutlinedIcon fontSize="small" />}
          onClick={onImportWorkflow}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: 2,
            '&:hover': { backgroundColor: 'action.hover', boxShadow: 3 },
            '&.Mui-disabled': { backgroundColor: 'background.paper' },
          }}
        >
           Import Procedure
        </Button>
      </Box>

      {/* ── Bottom-right: Zoom controls (vertical) ───────────────────── */}
      <Paper
        elevation={2}
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 1,
          overflow: 'hidden',
          zIndex: 10,
        }}
      >
        <Tooltip title="Zoom In" placement="left">
          <IconButton size="small" onClick={onZoomIn} sx={{ borderRadius: 0 }}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem />
        <Tooltip title="Zoom Out" placement="left">
          <IconButton size="small" onClick={onZoomOut} sx={{ borderRadius: 0 }}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem />
        <Tooltip title="Fit to Window" placement="left">
          <IconButton size="small" onClick={onZoomFit} sx={{ borderRadius: 0 }}>
            <CropFreeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem />
        <Tooltip title="Actual Size" placement="left">
          <IconButton size="small" onClick={onZoomActual} sx={{ borderRadius: 0 }}>
            <CropSquareIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </>
  );
};
